import { Script, Utils } from '@bsv/sdk';
/**
 * OrdLock PREFIX - sCrypt contract prefix shared with Lock template
 */
export const ORDLOCK_PREFIX = Utils.toArray('2097dfd76851bf465e8f715593b217714858bbe9570ff3bd5e33840a34e20ff0262102ba79df5f8ae7604a9830f03c7933028186aede0675a16f025dc4f8be8eec0382201008ce7480da41702918d1ec8e6849ba32b4d65b1e40dc669c31a1e6306b266c0000', 'hex');
/**
 * OrdLock SUFFIX - contract validation script
 */
export const ORDLOCK_SUFFIX = Utils.toArray('615179547a75537a537a537a0079537a75527a527a7575615579008763567901c161517957795779210ac407f0e4bd44bfc207355a778b046225a7068fc59ee7eda43ad905aadbffc800206c266b30e6a1319c66dc401e5bd6b432ba49688eecd118297041da8074ce081059795679615679aa0079610079517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e01007e81517a75615779567956795679567961537956795479577995939521414136d08c5ed2bf3ba048afe6dcaebafeffffffffffffffffffffffffffffff00517951796151795179970079009f63007952799367007968517a75517a75517a7561527a75517a517951795296a0630079527994527a75517a6853798277527982775379012080517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f517f7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e7c7e01205279947f7754537993527993013051797e527e54797e58797e527e53797e52797e57797e0079517a75517a75517a75517a75517a75517a75517a75517a75517a75517a75517a75517a75517a756100795779ac517a75517a75517a75517a75517a75517a75517a75517a75517a7561517a75517a756169587951797e58797eaa577961007982775179517958947f7551790128947f77517a75517a75618777777777777777777767557951876351795779a9876957795779ac777777777777777767006868', 'hex');
/**
 * Finds the index of a subarray within an array
 */
function indexOf(arr, subArr, fromIndex = 0) {
    for (let i = fromIndex; i <= arr.length - subArr.length; i++) {
        let found = true;
        for (let j = 0; j < subArr.length; j++) {
            if (arr[i + j] !== subArr[j]) {
                found = false;
                break;
            }
        }
        if (found)
            return i;
    }
    return -1;
}
/**
 * Reads a Bitcoin varint from a byte array
 * Returns the value and number of bytes consumed
 */
function readVarInt(data, offset) {
    const first = data[offset];
    if (first < 0xfd) {
        return { value: first, bytesRead: 1 };
    }
    else if (first === 0xfd) {
        const value = data[offset + 1] | (data[offset + 2] << 8);
        return { value, bytesRead: 3 };
    }
    else if (first === 0xfe) {
        const value = data[offset + 1] | (data[offset + 2] << 8) | (data[offset + 3] << 16) | (data[offset + 4] << 24);
        return { value, bytesRead: 5 };
    }
    else {
        // 0xff - 8 byte value, but we'll just handle up to 4 bytes for simplicity
        const value = data[offset + 1] | (data[offset + 2] << 8) | (data[offset + 3] << 16) | (data[offset + 4] << 24);
        return { value, bytesRead: 9 };
    }
}
/**
 * OrdLock - Ordinal Lock template for marketplace listings
 *
 * OrdLock enables trustless ordinal/NFT sales by locking an output with a
 * contract that can only be unlocked by providing payment to a specified address.
 *
 * @example
 * ```typescript
 * // Decode an OrdLock from a script
 * const ordlock = OrdLock.decode(script);
 * if (ordlock) {
 *   console.log(`Seller: ${ordlock.seller}`);
 *   console.log(`Price: ${ordlock.price} satoshis`);
 * }
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export default class OrdLock {
    /**
     * Decodes an OrdLock from a script
     *
     * @param script - The script to decode
     * @param mainnet - Whether to use mainnet address prefix (default: true)
     * @returns Decoded OrdLock data or null if not found/invalid
     */
    static decode(script, mainnet = true) {
        try {
            const scriptBinary = script.toBinary();
            // Find PREFIX
            const prefixIndex = indexOf(scriptBinary, ORDLOCK_PREFIX);
            if (prefixIndex === -1)
                return null;
            // Find SUFFIX after PREFIX
            const suffixIndex = indexOf(scriptBinary, ORDLOCK_SUFFIX, prefixIndex + ORDLOCK_PREFIX.length);
            if (suffixIndex === -1)
                return null;
            // Extract data between PREFIX and SUFFIX
            const dataBytes = scriptBinary.slice(prefixIndex + ORDLOCK_PREFIX.length, suffixIndex);
            if (dataBytes.length === 0)
                return null;
            // Parse the data as script chunks
            const dataScript = Script.fromBinary(dataBytes);
            const chunks = dataScript.chunks;
            if (chunks.length < 2)
                return null;
            // Chunk 0: seller PKHash (20 bytes)
            const sellerChunk = chunks[0];
            if (sellerChunk?.data == null || sellerChunk.data.length !== 20)
                return null;
            // Chunk 1: payout TransactionOutput (satoshis + locking script)
            const payoutChunk = chunks[1];
            if (payoutChunk?.data == null || payoutChunk.data.length < 9)
                return null;
            const payoutData = payoutChunk.data;
            // Parse TransactionOutput: 8-byte LE satoshis + varint script length + script
            // Read 8-byte little-endian satoshis
            let price = BigInt(0);
            for (let i = 0; i < 8; i++) {
                price |= BigInt(payoutData[i]) << BigInt(i * 8);
            }
            // Convert PKHash to address
            const addressPrefix = mainnet ? [0x00] : [0x6f];
            const seller = Utils.toBase58Check(sellerChunk.data, addressPrefix);
            return {
                seller,
                price,
                payout: Array.from(payoutData)
            };
        }
        catch {
            return null;
        }
    }
    /**
     * Checks if a script contains an OrdLock pattern
     *
     * @param script - The script to check
     * @returns true if the script contains OrdLock pattern
     */
    static isOrdLock(script) {
        const scriptBinary = script.toBinary();
        const prefixIndex = indexOf(scriptBinary, ORDLOCK_PREFIX);
        if (prefixIndex === -1)
            return false;
        const suffixIndex = indexOf(scriptBinary, ORDLOCK_SUFFIX, prefixIndex + ORDLOCK_PREFIX.length);
        return suffixIndex !== -1;
    }
    /**
     * Checks if an unlocking script indicates a purchase (vs cancellation)
     *
     * @param unlockingScript - The unlocking script to check
     * @returns true if the unlock represents a purchase
     */
    static isPurchase(unlockingScript) {
        const scriptBinary = unlockingScript.toBinary();
        return indexOf(scriptBinary, ORDLOCK_SUFFIX) !== -1;
    }
}
//# sourceMappingURL=OrdLock.js.map